/**
 * Helper function to check if the current URL contains media file extensions
 */
function checkUrlForMediaExtensions() {
    const url = window.location.href;
    const mediaExtensions = [
        '.mp4',
        '.mp3',
        '.avi',
        '.mov',
        '.wmv',
        '.flv',
        '.webm',
        '.mkv',
        '.pdf',
        '.jpg',
        '.jpeg',
        '.png',
        '.gif',
        '.svg',
        '.bmp',
        '.tiff',
    ];
    return mediaExtensions.some((ext) => url.toLowerCase().includes(ext));
}
/**
 * Check if the current page is a direct media file or non-HTML content
 * Uses MIME type detection via background script for robust filtering
 */
export async function isDirectMediaFile() {
    try {
        // Query the background script for the current tab's MIME type
        const response = await chrome.runtime.sendMessage({
            action: 'getMimeType',
        });
        if (response?.mimeType) {
            const mimeType = response.mimeType.toLowerCase();
            // Check if it's a non-HTML content type
            return (!mimeType.startsWith('text/html') &&
                !mimeType.startsWith('application/xhtml') &&
                (mimeType.startsWith('image/') ||
                    mimeType.startsWith('video/') ||
                    mimeType.startsWith('audio/') ||
                    mimeType === 'application/pdf' ||
                    mimeType.startsWith('application/octet-stream')));
        }
    }
    catch (error) {
        console.warn('Failed to detect MIME type, falling back to URL-based detection:', error);
    }
    // Single fallback point for all cases
    return checkUrlForMediaExtensions();
}
/**
 * Check if we're in a sandboxed context
 */
export function isSandboxed() {
    try {
        // Also check for sandbox attribute in parent frame
        if (window.frameElement) {
            const sandbox = window.frameElement.getAttribute('sandbox');
            if (sandbox !== null) {
                // If sandbox attribute exists and doesn't allow scripts, we're sandboxed
                return !sandbox.includes('allow-scripts');
            }
        }
        // Additional check: try to access parent window properties
        // This will throw in truly sandboxed contexts
        if (window.parent !== window) {
            window.parent.location.href; // This will throw if sandboxed
        }
        return false;
    }
    catch (e) {
        // Only return true if we get a security error suggesting actual sandboxing
        const errorMessage = e instanceof Error ? e.message.toLowerCase() : '';
        return (errorMessage.includes('sandbox') ||
            errorMessage.includes('blocked') ||
            errorMessage.includes('denied'));
    }
}
/**
 * Initialize page injection logic - determines if injection should proceed
 * and handles the injection process if appropriate
 */
export async function initializePageInjection(injectParentScript, registerPageWithBackground) {
    // Skip injection on direct media files or sandboxed contexts
    if (!(await isDirectMediaFile()) && !isSandboxed()) {
        // Generate unique page ID for this page
        const pageId = crypto.randomUUID();
        // Start injection when DOM is ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => injectParentScript(pageId));
        }
        else {
            injectParentScript(pageId);
        }
        // Register this page with the background script for tracking
        registerPageWithBackground(pageId);
    }
}
